/**
 * Session and token refresh.
 *
 * ── The single most important thing in this file ───────────────────────────
 *
 * **Every refresh goes through one in-flight promise.** This is not an
 * optimisation. It is the difference between a working app and one that
 * randomly logs people out of every device.
 *
 * The auth-service treats a re-presented refresh token as evidence of theft and
 * revokes the entire rotation family — which is correct behaviour, and a trap.
 * If the app fires five requests in parallel and all five get a 401, all five
 * call `/auth/refresh`. The first rotates successfully. The other four present
 * a token that has just been revoked. The service concludes the token was
 * stolen and destroys the family.
 *
 * The user experiences this as being signed out of everything, for no reason,
 * occasionally. It is very hard to reproduce deliberately and very easy to
 * ship.
 *
 * The runbook flags this as "the step most likely to be skipped". Do not skip
 * it. If you add a second code path that calls the refresh endpoint, route it
 * through `refreshAccessToken()` here.
 *
 * ── Token storage ─────────────────────────────────────────────────────────
 *
 * Tokens live in memory, with the refresh token additionally in localStorage so
 * a page reload does not sign the user out.
 *
 * localStorage is readable by any script on this origin, so this trades some
 * XSS exposure for not making people log in every time they refresh. The
 * mitigations that make that trade acceptable: a short access-token TTL (~30
 * min), refresh-token rotation, and a strict CSP. The native clients use
 * Keychain/Keystore instead and should never use anything weaker.
 */

import { UserFacingError } from '../user-facing-error.js';
const REFRESH_KEY = 'voxfauna.refresh';
const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? 'https://auth.voxfauna.org';
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID ?? 'voxfauna';

export class SessionExpiredError extends UserFacingError {
  constructor() {
    super('Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

/** In memory only. Never persisted — it is short-lived by design. */
let accessToken: string | null = null;

/** The single in-flight refresh. See the note at the top of this file. */
let inFlight: Promise<string> | null = null;

type Listener = (signedIn: boolean) => void;
const listeners = new Set<Listener>();

function announce(signedIn: boolean): void {
  for (const listener of listeners) listener(signedIn);
}

export const session = {
  onChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  hasSession(): boolean {
    return accessToken !== null || readRefreshToken() !== null;
  },

  getAccessToken(): string | null {
    return accessToken;
  },

  /** Called after a successful sign-in. */
  start(tokens: { token: string; refreshToken: string }): void {
    accessToken = tokens.token;
    writeRefreshToken(tokens.refreshToken);
    announce(true);
  },

  clear(): void {
    accessToken = null;
    writeRefreshToken(null);
    announce(false);
  },

  /**
   * A valid access token, refreshing if necessary.
   *
   * Concurrent callers join the same refresh rather than each starting one.
   */
  async getValidAccessToken(): Promise<string> {
    if (accessToken) return accessToken;
    return refreshAccessToken();
  },
};

/**
 * Rotates the refresh token and returns a new access token.
 *
 * Concurrent callers receive the same promise. Exported so that the fetch
 * wrapper's 401 handler uses this path and only this path.
 */
export function refreshAccessToken(): Promise<string> {
  // Join the refresh already running, rather than starting a second one.
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const refreshToken = readRefreshToken();
    if (!refreshToken) throw new SessionExpiredError();

    let response: Response;
    try {
      // auth.voxfauna.org is a CloudFront distribution whose Origin Path (/auth) rewrites this
      // onto the real auth-service's app.use('/auth', ...) mount — no prefix needed here.
      response = await fetch(`${AUTH_BASE}/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Id': CLIENT_ID,
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // A network failure is not an expired session. Clearing the session here
      // would sign people out every time their train enters a tunnel.
      throw new Error('Could not reach the sign-in service. Check your connection.');
    }

    if (!response.ok) {
      session.clear();
      throw new SessionExpiredError();
    }

    const body = (await response.json()) as {
      data?: { token: string; refreshToken: string };
      token?: string;
      refreshToken?: string;
    };

    // The auth-service wraps successful responses in `data`. Tolerate both
    // shapes rather than breaking on an envelope change.
    const token = body.data?.token ?? body.token;
    const rotated = body.data?.refreshToken ?? body.refreshToken;

    if (!token || !rotated) {
      session.clear();
      throw new SessionExpiredError();
    }

    accessToken = token;
    // Persisting the ROTATED token is mandatory. Dropping it locks the user out
    // on the next refresh, because the old one is already revoked.
    writeRefreshToken(rotated);

    return token;
  })();

  // Clear the slot whether it resolved or rejected, so a later attempt can
  // start a fresh refresh rather than re-awaiting a failed one forever.
  inFlight = inFlight.finally(() => {
    inFlight = null;
  }) as Promise<string>;

  return inFlight;
}

/** Discards the in-memory access token so the next call refreshes. */
export function invalidateAccessToken(): void {
  accessToken = null;
}

function readRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_KEY);
  } catch {
    // Private browsing, blocked site data, or an embedded context. The app
    // still works; the user just signs in again after a reload.
    return null;
  }
}

function writeRefreshToken(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(REFRESH_KEY);
    else window.localStorage.setItem(REFRESH_KEY, value);
  } catch {
    /* see readRefreshToken */
  }
}
